/** Plan persistence — localStorage, no account. Schema is versioned from
 * day one; quota/private-mode failures degrade to in-memory (the session
 * keeps working, persistence just doesn't stick). */

import type { Plan, PlanStats, SavedPlan } from "./types";

export const PLANS_KEY = "dustlark.plans.v1";
export const DRAFT_KEY = "dustlark.plandraft.v1";

interface PlansDoc {
  v: 1;
  plans: SavedPlan[];
}

// In-memory fallback, keyed by storage key. Populated whenever a real
// write misses localStorage (quota, private mode, or no localStorage at
// all) so the session keeps working.
const memory = new Map<string, string>();
// Keys whose most recent write did NOT reach real storage — reads for
// these must prefer `memory` even though localStorage might still hold a
// stale (or absent) value for the same key.
const degraded = new Set<string>();

function getStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}

function readRaw(key: string): string | null {
  const ls = getStorage();
  if (ls && !degraded.has(key)) {
    try {
      return ls.getItem(key);
    } catch {
      degraded.add(key);
    }
  }
  return memory.has(key) ? (memory.get(key) as string) : null;
}

function writeRaw(key: string, value: string): void {
  const ls = getStorage();
  if (ls) {
    try {
      ls.setItem(key, value);
      degraded.delete(key);
      memory.delete(key);
      return;
    } catch {
      // Quota exceeded or private-mode write rejection — degrade below.
    }
  }
  degraded.add(key);
  memory.set(key, value);
}

function removeRaw(key: string): void {
  const ls = getStorage();
  if (ls) {
    try {
      ls.removeItem(key);
    } catch {
      // ignore — memory/degraded cleared below regardless
    }
  }
  degraded.delete(key);
  memory.delete(key);
}

function readPlansDoc(): PlansDoc {
  const raw = readRaw(PLANS_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && parsed.v === 1 && Array.isArray(parsed.plans)) {
        return parsed as PlansDoc;
      }
    } catch {
      // corrupt JSON — fall through to an empty doc
    }
  }
  return { v: 1, plans: [] };
}

function writePlansDoc(doc: PlansDoc): void {
  writeRaw(PLANS_KEY, JSON.stringify(doc));
}

function newId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "p" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function listPlans(): SavedPlan[] {
  const doc = readPlansDoc();
  return [...doc.plans].sort((a, b) => (a.savedAt > b.savedAt ? -1 : a.savedAt < b.savedAt ? 1 : 0));
}

/** Upsert: with `id`, overwrites that plan; without, creates a new one. */
export function savePlan(plan: Plan, stats: PlanStats, id?: string): SavedPlan {
  const doc = readPlansDoc();
  const saved: SavedPlan = {
    ...plan,
    id: id ?? newId(),
    savedAt: new Date().toISOString(),
    stats,
  };
  const idx = id ? doc.plans.findIndex((p) => p.id === id) : -1;
  if (idx >= 0) {
    doc.plans[idx] = saved;
  } else {
    doc.plans.push(saved);
  }
  writePlansDoc(doc);
  return saved;
}

export function getPlan(id: string): SavedPlan | null {
  const doc = readPlansDoc();
  return doc.plans.find((p) => p.id === id) ?? null;
}

export function deletePlan(id: string): void {
  const doc = readPlansDoc();
  doc.plans = doc.plans.filter((p) => p.id !== id);
  writePlansDoc(doc);
}

/** The in-progress route, autosaved on every edit; null clears it. */
export function saveDraft(plan: Plan | null): void {
  if (plan === null) {
    removeRaw(DRAFT_KEY);
    return;
  }
  writeRaw(DRAFT_KEY, JSON.stringify(plan));
}

export function loadDraft(): Plan | null {
  const raw = readRaw(DRAFT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Plan;
  } catch {
    return null;
  }
}
